import * as React from "react"
import { AlertTriangle, Check, Download, FileText, Info, Loader2, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useBookingT } from "@/hooks/use-translations"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import {
  acceptDocument,
  fetchDocumentBody,
  fetchLegalState,
  withdrawConsent,
  DocumentBody,
  LegalDocument,
  LegalState,
  OutstandingItem,
  StandingConsent,
} from "./api"
import { sampleDocumentBody, sampleLegalState } from "./sample"
import { ProductActivationDialog } from "./ProductActivationDialog"

/** Development-only fallback so the page can be reviewed before the API ships. */
const isDev = process.env.NODE_ENV === "development"

/** What a customer calls the product, not what the enum calls it. */
function productLabel(t: (key: string, fallback: string) => string, productCode: string): string {
  if (productCode === "CARD") return t("legal_product_card", "the card programme")
  if (productCode === "TRAVEL") return t("legal_product_travel", "travel booking")
  if (productCode === "EXPENSE") return t("legal_product_expense", "expense management")
  if (productCode === "HRIS") return t("legal_product_hris", "the HR system integration")
  return productCode
}

/** Days left in the 30-day window, floored, never negative. */
function daysLeft(deadlineTs: number | null): number | null {
  if (!deadlineTs) return null
  return Math.max(0, Math.floor((deadlineTs * 1000 - Date.now()) / 86400000))
}

function formatDate(value?: string | null): string {
  if (!value) return "—"
  const parsed = new Date(String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`)
  if (Number.isNaN(parsed.getTime())) return "—"
  return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

/* ------------------------------------------------------------------ */
/* Outstanding banner                                                  */
/* ------------------------------------------------------------------ */

function OutstandingBanner({
  state,
  onReview,
}: {
  state: LegalState
  onReview: () => void
}) {
  const t = useBookingT()
  const blocking = state.outstanding.filter((i) => i.changeType === "material" && i.overdue)
  const pending = state.outstanding.filter((i) => i.changeType === "material" && !i.overdue)
  const notices = state.outstanding.filter((i) => i.changeType !== "material")
  const [noticeDismissed, setNoticeDismissed] = React.useState(false)

  if (blocking.length > 0) {
    const restricted = state.restriction.account
      ? t("legal_restricted_account", "New bookings, card issuance and new activations are paused.")
      : t("legal_restricted_products", "Affected features are paused: ") + state.restriction.products.join(", ")

    return (
      <div className="rounded-xl border border-error-300 bg-error-100 p-4 space-y-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-error-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-neutral-900">
              {t("legal_out_of_date", "Your agreements are out of date.")}
            </p>
            {/* Only what actually stops. Existing bookings, exports and admin
                keep working — the contract does not allow more than this. */}
            <p className="text-sm text-neutral-800">{restricted}</p>
            <p className="text-sm text-neutral-700">
              {t("legal_still_available", "Trips already booked, existing cards, your data and exports are unaffected.")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pl-7">
          <Button size="sm" onClick={onReview}>
            {t("legal_resign_now", "Re-sign now")}
          </Button>
          {/* Master Agreement 14.4 gives the right to end the affected Schedule
              without penalty. If this only led back to a signature, we would be
              offering an exit in the contract that the product does not deliver. */}
          <Button size="sm" variant="outline" asChild>
            <a href="/contacts">{t("legal_contact_us", "Contact us")}</a>
          </Button>
        </div>
      </div>
    )
  }

  if (pending.length > 0) {
    const left = daysLeft(state.nearestDeadline)

    return (
      <div className="rounded-xl border border-warning-300 bg-warning-100 p-4 space-y-2">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-warning-700 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-neutral-900">
              {t("legal_updated_title", "We've updated your agreements")}
            </p>
            <p className="text-sm text-neutral-800">
              {pending.map((i) => i.name).join(", ")}
              {left !== null && (
                <>
                  {" · "}
                  <span className="font-medium">
                    {left} {t("legal_days_left", "days left to accept")}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="pl-7">
          <Button size="sm" onClick={onReview}>
            {t("legal_review_changes", "Review changes")}
          </Button>
        </div>
      </div>
    )
  }

  if (notices.length > 0 && !noticeDismissed) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-white p-4 flex items-start gap-2">
        <Info className="h-5 w-5 text-neutral-500 shrink-0 mt-0.5" />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-neutral-900">
            {t("legal_minor_updates", "Minor updates to your agreements")}
          </p>
          <p className="text-sm text-neutral-700">{notices.map((i) => i.name).join(", ")}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setNoticeDismissed(true)}>
          {t("legal_dismiss", "Dismiss")}
        </Button>
      </div>
    )
  }

  return null
}

/* ------------------------------------------------------------------ */
/* Document viewer                                                     */
/* ------------------------------------------------------------------ */

export function DocumentViewer({
  target,
  sample,
  onOpenChange,
}: {
  target: { code: string; name: string; versionId?: number } | null
  sample: boolean
  onOpenChange: (v: boolean) => void
}) {
  const t = useBookingT()
  const [doc, setDoc] = React.useState<DocumentBody | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!target) return
    setDoc(null)
    setError(null)
    setLoading(true)
    fetchDocumentBody(target.code, target.versionId)
      .then(setDoc)
      .catch((e: Error) => {
        if (sample) {
          setDoc({
            code: target.code, name: target.name, versionId: target.versionId || 0,
            versionLabel: "—", effectiveFrom: "", changeSummary: null,
            accepted: false, body: sampleDocumentBody(target.name),
          })
          return
        }
        setError(e.message)
      })
      .finally(() => setLoading(false))
  }, [target, sample])

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{doc?.name || target?.name}</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-warning-300 bg-warning-100 px-3 py-2 text-sm text-warning-800">
            {error}
          </div>
        )}

        {doc && (
          <>
            <p className="text-sm text-neutral-700">
              {t("legal_version", "Version")} {doc.versionLabel}
              {doc.accepted && ` · ${t("legal_your_copy", "your accepted copy")}`}
            </p>
            {/* The stored document is HTML produced by our own template engine
                and rendered server-side; it is not user input. */}
            <div
              className="prose prose-sm max-w-none text-neutral-900 [&_h1]:text-lg [&_h2]:text-base [&_h2]:font-semibold [&_p]:my-2 [&_table]:w-full [&_td]:border [&_td]:border-neutral-200 [&_td]:p-1.5"
              dangerouslySetInnerHTML={{ __html: doc.body }}
            />
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("legal_close", "Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ */
/* Accept dialog                                                       */
/* ------------------------------------------------------------------ */

function AcceptDialog({
  item,
  signer,
  open,
  onOpenChange,
  onAccepted,
  onRead,
}: {
  item: OutstandingItem | null
  signer: { name: string | null; title: string | null }
  open: boolean
  onOpenChange: (v: boolean) => void
  onAccepted: () => void
  onRead: (target: { code: string; name: string; versionId?: number }) => void
}) {
  const t = useBookingT()
  const [agreed, setAgreed] = React.useState(false)
  const [authority, setAuthority] = React.useState(false)
  const [signerTitle, setSignerTitle] = React.useState("")
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setAgreed(false)
    setAuthority(false)
    setSignerTitle(signer.title || "")
  }, [open, signer.title])

  if (!item) return null

  // Stored verbatim with the acceptance. Whether the act was unambiguous gets
  // judged later from this string, so it names the document rather than saying
  // "the terms". The colon keeps it grammatical for every title — "agree to the
  // Schedule A" is not English, and the wording cannot depend on the document.
  const affirmation = t("legal_affirm_prefix", "I have read and agree to:") + " " + item.name

  const submit = async () => {
    if (!agreed || !authority || saving) return
    setSaving(true)
    try {
      await acceptDocument({
        versionId: item.versionId,
        affirmationText: affirmation,
        signerTitle: signerTitle.trim(),
        authorityAffirmed: authority,
      })
      toast.success(t("legal_accepted", "Agreement accepted"))
      onAccepted()
      onOpenChange(false)
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{item.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {item.summary && (
            <div className="rounded-lg bg-neutral-100 border border-neutral-200 px-3 py-2.5 space-y-1">
              <p className="text-sm font-semibold text-neutral-900">
                {t("legal_what_changed", "What changed")}
              </p>
              <p className="text-sm text-neutral-700">{item.summary}</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => onRead({ code: item.code, name: item.name, versionId: item.versionId })}
            className="inline-flex items-center gap-1.5 text-sm text-brand-600 underline underline-offset-2"
          >
            <FileText className="h-4 w-4" />
            {t("legal_read_full", "Read the full document")}
          </button>

          {/* The name is not a field. It comes from the account, so an
              acceptance cannot be made out to somebody else — which would also
              contradict the user id stored on the same record. The job title is
              a claim about this person's role, not their identity, so it stays
              editable. */}
          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
            <div className="space-y-2">
              <Label>{t("legal_signing_as", "Signing as")}</Label>
              <div className="h-10 flex items-center rounded-md border border-neutral-200 bg-neutral-100 px-3 text-sm text-neutral-900">
                {signer.name || "—"}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="legal-title">{t("legal_signer_title", "Your title")}</Label>
              <Input
                id="legal-title"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder={t("legal_signer_title_ph", "e.g. Chief Executive Officer")}
              />
            </div>
          </div>

          {/* Two separate affirmative acts: agreement to the document, and the
              authority to bind the company. The second is stored as its own
              boolean rather than as prose inside the contract. */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
            <span className="text-sm text-neutral-900">{affirmation}</span>
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={authority} onCheckedChange={(v) => setAuthority(v === true)} className="mt-0.5" />
            <span className="text-sm text-neutral-900">
              {t("legal_affirm_authority", "I am authorised to enter into this agreement on behalf of my company.")}
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("btn_cancel", "Cancel")}
          </Button>
          <Button onClick={submit} disabled={!agreed || !authority || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("legal_accept", "Accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ */
/* Document row                                                        */
/* ------------------------------------------------------------------ */

function DocumentRow({
  document,
  outstanding,
  maySign,
  onAccept,
  onRead,
}: {
  document: LegalDocument
  outstanding?: OutstandingItem
  maySign: boolean
  onAccept: (item: OutstandingItem) => void
  onRead: (target: { code: string; name: string; versionId?: number }) => void
}) {
  const t = useBookingT()

  return (
    // Wrapping rather than stacking: on a narrow screen the actions drop to
    // their own full-width line while the icon stays beside the title, instead
    // of the icon sitting alone on a line of its own.
    <div className="bg-white rounded-xl border border-neutral-200 p-4 flex items-start gap-3 flex-wrap">
      <div className="h-10 w-10 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
        <FileText className="h-5 w-5 text-brand-600" />
      </div>

      <div className="flex-1 min-w-[220px] space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold text-neutral-900">{document.name}</span>
          {document.accepted && !outstanding && (
            <Badge variant="green">
              <Check className="h-3 w-3 mr-1" />
              {t("legal_in_force", "In force")}
            </Badge>
          )}
          {outstanding?.changeType === "material" && (
            <Badge variant={outstanding.overdue ? "destructive" : "warning"}>
              {outstanding.overdue
                ? t("legal_overdue", "Overdue")
                : t("legal_action_needed", "Action needed")}
            </Badge>
          )}
          {outstanding?.changeType === "non_material" && (
            <Badge variant="secondary">{t("legal_updated", "Updated")}</Badge>
          )}
        </div>

        {document.plainLanguageSummary && (
          <p className="text-sm text-neutral-700">{document.plainLanguageSummary}</p>
        )}

        <p className="text-sm text-neutral-700">
          {document.accepted ? (
            <>
              {t("legal_version", "Version")} {document.accepted.versionLabel} ·{" "}
              {t("legal_accepted_on", "accepted")} {formatDate(document.accepted.acceptedAt)}
              {document.accepted.signerName ? ` · ${document.accepted.signerName}` : ""}
            </>
          ) : (
            t("legal_not_accepted", "Not accepted yet")
          )}
        </p>
      </div>

      <div className="flex items-center gap-2 shrink-0 max-md:w-full max-md:pt-1">
        <Button size="sm" variant="outline" onClick={() => onRead({
          code: document.code,
          name: document.name,
          versionId: document.accepted?.versionId ?? document.currentVersion?.id,
        })}>
          {t("legal_view", "View")}
        </Button>
        {/* Only offered when a file actually exists. A download button that
            does nothing is worse than no button. */}
        {document.accepted?.pdfUrl && (
          <Button size="sm" variant="outline" asChild>
            <a href={document.accepted.pdfUrl} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4 mr-1.5" />
              PDF
            </a>
          </Button>
        )}
        {outstanding && outstanding.changeType === "material" && maySign && (
          <Button size="sm" onClick={() => onAccept(outstanding)}>
            {outstanding.firstSeen ? t("legal_accept", "Accept") : t("legal_review_accept", "Review & accept")}
          </Button>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Standing permissions                                                */
/* ------------------------------------------------------------------ */

/**
 * Permissions the customer gave separately, and can take back separately.
 *
 * Schedule C section 5.3 gives the right to withdraw the card data-sharing
 * consent. A right with no control in the product is not a right the customer
 * has, so this section exists even though it is usually one row — and it says
 * what withdrawing costs before the button, not in a toast afterwards.
 */
function ConsentSection({
  consents,
  maySign,
  onWithdrawn,
}: {
  consents: StandingConsent[]
  maySign: boolean
  onWithdrawn: () => void
}) {
  const t = useBookingT()
  const [confirming, setConfirming] = React.useState<StandingConsent | null>(null)
  const [saving, setSaving] = React.useState(false)

  if (consents.length === 0) return null

  const submit = async () => {
    if (!confirming || saving) return
    setSaving(true)
    try {
      await withdrawConsent(confirming.consentCode)
      toast.success(t("legal_consent_withdrawn", "Permission withdrawn"))
      setConfirming(null)
      onWithdrawn()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">{t("legal_permissions", "Permissions you gave")}</h2>

      {consents.map((consent) => (
        <div
          key={consent.consentCode}
          className="bg-white rounded-xl border border-neutral-200 p-4 flex items-start gap-3 flex-wrap"
        >
          <div className="h-10 w-10 rounded-lg bg-success-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-success-600" />
          </div>
          <div className="flex-1 min-w-[220px] space-y-1">
            <span className="text-base font-semibold text-neutral-900">{consent.name}</span>
            {consent.summary && <p className="text-sm text-neutral-700">{consent.summary}</p>}
            <p className="text-sm text-neutral-700">
              {t("legal_granted_on", "Given")} {formatDate(consent.grantedAt)}
            </p>
          </div>
          {maySign && (
            <div className="shrink-0 max-md:w-full max-md:pt-1">
              <Button size="sm" variant="outline" onClick={() => setConfirming(consent)}>
                {t("legal_withdraw", "Withdraw")}
              </Button>
            </div>
          )}
        </div>
      ))}

      <Dialog open={confirming !== null} onOpenChange={(v) => !v && setConfirming(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {t("legal_withdraw_title", "Withdraw this permission?")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-900">
            {confirming?.withdrawal ||
              t("legal_withdraw_generic", "This permission will end and cannot be un-withdrawn — you would have to give it again.")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)} disabled={saving}>
              {t("btn_cancel", "Cancel")}
            </Button>
            <Button variant="destructive" onClick={submit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("legal_withdraw", "Withdraw")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function LegalDocumentsPage() {
  const t = useBookingT()
  const [state, setState] = React.useState<LegalState | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [usingSample, setUsingSample] = React.useState(false)
  const [accepting, setAccepting] = React.useState<OutstandingItem | null>(null)
  const [reading, setReading] = React.useState<{ code: string; name: string; versionId?: number } | null>(null)

  const [activating, setActivating] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setState(await fetchLegalState())
      setUsingSample(false)
      setError(null)
    } catch (e) {
      // On a development build with no API behind it, show the page with sample
      // data and say so, rather than an error nobody can act on. Production
      // shows the error, because there it means something is actually wrong.
      if (isDev) {
        setState(sampleLegalState())
        setUsingSample(true)
        setError(null)
      } else {
        setError((e as Error).message)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  const outstandingByCode = React.useMemo(() => {
    const map: Record<string, OutstandingItem> = {}
    for (const item of state?.outstanding || []) map[item.code] = item
    return map
  }, [state])

  const firstActionable = (state?.outstanding || []).find((i) => i.changeType === "material")

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("legal_page_title", "Documents")}</h1>
        <p className="text-sm text-neutral-700 mt-1">
          {t("legal_page_subtitle", "Your agreements with Travel Code — what is in force, what you accepted, and when.")}
        </p>
      </div>

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-warning-300 bg-warning-100 px-4 py-3 text-sm text-warning-800 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {!loading && usingSample && (
        <div className="rounded-xl border border-neutral-300 bg-neutral-100 px-4 py-2.5 text-sm text-neutral-800">
          {t("legal_sample_notice", "Showing sample data — the documents API is not available in this environment.")}
        </div>
      )}

      {!loading && state && (
        <>
          <OutstandingBanner
            state={state}
            onReview={() => firstActionable && setAccepting(firstActionable)}
          />

          {/* Somebody has to be able to act. Telling the rest of the team who
              that is beats showing them a button that always refuses. */}
          {!state.maySign && state.requiresAction && (
            <div className="rounded-xl border border-neutral-200 bg-white p-4 flex items-start gap-2">
              <ShieldCheck className="h-5 w-5 text-neutral-500 shrink-0 mt-0.5" />
              <p className="text-sm text-neutral-700">
                {t(
                  "legal_owner_only",
                  "Accepting agreements binds the company, so only the account owner can do it. Ask them to review the documents below."
                )}
              </p>
            </div>
          )}

          {state.documents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-10 w-10 text-neutral-500 mb-3" />
              <h3 className="text-base font-semibold text-neutral-900">
                {t("legal_empty_title", "No documents yet")}
              </h3>
              <p className="text-sm text-neutral-700 mt-1">
                {t("legal_empty_hint", "Agreements appear here as you activate products.")}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {state.documents.map((document) => (
              <DocumentRow
                key={document.code}
                document={document}
                outstanding={outstandingByCode[document.code]}
                maySign={state.maySign}
                onAccept={setAccepting}
                onRead={setReading}
              />
            ))}
          </div>

          {/* Somebody who accepted four of the eight card documents and closed
              the tab has no other route back to the fifth. This is that route. */}
          {state.pendingActivations.map((pending) => (
            <div
              key={pending.productCode}
              className="rounded-xl border border-brand-200 bg-brand-100 p-4 flex items-start gap-3 flex-wrap"
            >
              <FileText className="h-5 w-5 text-brand-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[220px] space-y-1">
                <p className="text-sm font-semibold text-neutral-900">
                  {t("legal_activation_pending", "You have paperwork left to finish")}
                </p>
                <p className="text-sm text-neutral-800">
                  {productLabel(t, pending.productCode)} · {pending.done}/{pending.total}{" "}
                  {t("legal_activation_steps_done", "steps done")}
                </p>
              </div>
              {state.maySign && (
                <div className="shrink-0 max-md:w-full max-md:pt-1">
                  <Button size="sm" onClick={() => setActivating(pending.productCode)}>
                    {t("legal_activation_continue", "Continue")}
                  </Button>
                </div>
              )}
            </div>
          ))}

          <ConsentSection
            consents={state.consents}
            maySign={state.maySign}
            onWithdrawn={load}
          />

          <p className={cn("text-sm text-neutral-700", state.documents.length === 0 && "hidden")}>
            {t("legal_archive_hint", "Current and past versions of our standard documents are published in the")}{" "}
            <a href="/legal" className="text-brand-600 underline underline-offset-2">
              {t("legal_archive_link", "legal archive")}
            </a>
            .
          </p>
        </>
      )}

      <AcceptDialog
        item={accepting}
        signer={state?.signer || { name: null, title: null }}
        open={accepting !== null}
        onOpenChange={(v) => !v && setAccepting(null)}
        onAccepted={load}
        onRead={setReading}
      />

      <DocumentViewer
        target={reading}
        sample={usingSample}
        onOpenChange={(v) => !v && setReading(null)}
      />

      {activating && (
        <ProductActivationDialog
          productCode={activating}
          productName={productLabel(t, activating)}
          open
          onOpenChange={(v) => {
            if (v) return
            setActivating(null)
            void load()
          }}
          onRead={setReading}
        />
      )}
    </div>
  )
}

export default LegalDocumentsPage
